import {
  CONTAINS_FILTER_PREFIX,
  displayValuesEqual,
} from "@/components/non_registrant/dashboard-column-filter-match";

/**
 * Column ids that display the same jurisdiction or license type as the header
 * filters. The jurisdictions list uses `name`, which is too generic to list here.
 */
export const GLOBAL_LINKED_COLUMN_IDS = [
  "jurisdiction.name",
  "jurisdiction_name",
  "jurisdictionName",
  "license_type.name",
  "license_type_name",
  "license_type",
  "licenseTypeName",
  "license_types",
] as const;

export type NamedIdOption = {
  id: string;
  name: string;
};

export type ColumnValueMapping =
  | { kind: "empty" }
  | { kind: "exact"; ids: string[] }
  | { kind: "unmapped" };

export function columnFilterValuesEqual(
  left: string[] | undefined,
  right: string[] | undefined,
): boolean {
  const a = left ?? [];
  const b = right ?? [];
  if (a.length !== b.length) return false;
  const used = new Array<boolean>(b.length).fill(false);
  return a.every((value) => {
    const index = b.findIndex(
      (candidate, candidateIndex) =>
        !used[candidateIndex] && displayValuesEqual(candidate, value),
    );
    if (index < 0) return false;
    used[index] = true;
    return true;
  });
}

export function sameIdSet(left: string[], right: string[]): boolean {
  if (left.length !== right.length) return false;
  const rightIds = new Set(right);
  return left.every((id) => rightIds.has(id));
}

export function namesForIds(
  ids: readonly string[],
  options: readonly NamedIdOption[],
): string[] {
  const names: string[] = [];
  for (const id of ids) {
    const option = options.find((candidate) => candidate.id === id);
    if (!option?.name) continue;
    if (names.some((name) => displayValuesEqual(name, option.name))) continue;
    names.push(option.name);
  }
  return names;
}

export function mapColumnValuesToIds(
  values: readonly string[] | undefined,
  options: readonly NamedIdOption[],
): ColumnValueMapping {
  if (!values || values.length === 0) return { kind: "empty" };
  const ids: string[] = [];
  for (const value of values) {
    if (value.startsWith(CONTAINS_FILTER_PREFIX)) return { kind: "unmapped" };
    const matches = options.filter((option) =>
      displayValuesEqual(option.name, value),
    );
    if (matches.length === 0) return { kind: "unmapped" };
    for (const match of matches) {
      if (!ids.includes(match.id)) ids.push(match.id);
    }
  }
  return { kind: "exact", ids };
}

function exactNames(
  values: string[] | undefined,
  options: readonly NamedIdOption[],
): string[] | null {
  const mapped = mapColumnValuesToIds(values, options);
  if (mapped.kind !== "exact") return null;
  const names = namesForIds(mapped.ids, options);
  return names.length > 0 ? names : null;
}

function isNameSubset(
  subset: readonly string[],
  superset: readonly string[],
): boolean {
  return subset.every((name) =>
    superset.some((candidate) => displayValuesEqual(candidate, name)),
  );
}

function everyIdMaps(
  ids: readonly string[],
  options: readonly NamedIdOption[],
): boolean {
  return ids.every((id) => options.some((option) => option.id === id));
}

export function withLinkedColumnValue(
  filters: Record<string, string[]>,
  columnId: string,
  values: string[] | undefined,
): Record<string, string[]> {
  const nextValues = (values ?? []).filter((value) => value.length > 0);
  const current = filters[columnId];
  if (nextValues.length === 0) {
    if (!current || current.length === 0) return filters;
    const next = { ...filters };
    delete next[columnId];
    return next;
  }
  if (columnFilterValuesEqual(current, nextValues)) return filters;
  return { ...filters, [columnId]: nextValues };
}

/**
 * Keep header multi-selects and the matching column/map facets on one selection.
 * `columns[i]` is the linked column's values, or undefined when that column is unset.
 */
export function reconcileLinkedColumnFilters({
  selectedIds,
  columns,
  options,
  globalChanged,
  columnChanged,
  adoptFromColumns,
  initializing,
}: {
  selectedIds: string[];
  columns: Array<string[] | undefined>;
  options: readonly NamedIdOption[];
  globalChanged: boolean;
  columnChanged: boolean[];
  adoptFromColumns: boolean;
  initializing: boolean;
}): { selectedIds: string[]; columns: Array<string[] | undefined> } {
  const globalNames = namesForIds(selectedIds, options);
  const writeNames = (names: string[]) =>
    columns.map(() => (names.length > 0 ? names : undefined));

  if (initializing) {
    if (selectedIds.length > 0 && !everyIdMaps(selectedIds, options)) {
      return { selectedIds, columns };
    }
    if (selectedIds.length > 0) {
      if (adoptFromColumns) {
        const narrower = columns
          .map((values) => exactNames(values, options))
          .find(
            (names) =>
              names !== null &&
              isNameSubset(names, globalNames) &&
              !columnFilterValuesEqual(names, globalNames),
          );
        if (narrower) {
          const mapped = mapColumnValuesToIds(narrower, options);
          if (mapped.kind === "exact") {
            return {
              selectedIds: mapped.ids,
              columns: writeNames(namesForIds(mapped.ids, options)),
            };
          }
        }
      }
      return { selectedIds, columns: writeNames(globalNames) };
    }
    if (adoptFromColumns) {
      const adopted = columns
        .map((values) => exactNames(values, options))
        .find((names) => names !== null);
      if (adopted) {
        const mapped = mapColumnValuesToIds(adopted, options);
        if (mapped.kind === "exact") {
          return {
            selectedIds: mapped.ids,
            columns: writeNames(namesForIds(mapped.ids, options)),
          };
        }
      }
    }
    return { selectedIds, columns };
  }

  if (columnChanged.some(Boolean) && !globalChanged) {
    const index = columnChanged.findIndex(Boolean);
    const mapped = mapColumnValuesToIds(columns[index], options);
    if (mapped.kind === "unmapped") {
      return {
        selectedIds: [],
        columns: columns.map((values, columnIndex) =>
          columnIndex === index ? values : undefined,
        ),
      };
    }
    const ids = mapped.kind === "empty" ? [] : mapped.ids;
    return { selectedIds: ids, columns: writeNames(namesForIds(ids, options)) };
  }

  return { selectedIds, columns: writeNames(globalNames) };
}

/** Keep jurisdiction/license column values when a tile replaces the other filters. */
export function preserveLinkedColumnFilters(
  current: Record<string, string[]>,
  next: Record<string, string[]>,
): Record<string, string[]> {
  const preserved: Record<string, string[]> = {};
  for (const columnId of GLOBAL_LINKED_COLUMN_IDS) {
    const values = current[columnId];
    if (values && values.length > 0) preserved[columnId] = values;
  }
  return { ...preserved, ...next };
}

export function omitGloballyLinkedColumnFilters(
  filters: Record<string, string[]>,
): Record<string, string[]> {
  const next = { ...filters };
  for (const columnId of GLOBAL_LINKED_COLUMN_IDS) {
    delete next[columnId];
  }
  return next;
}
